import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Single-page navigation doesn't reload the document, so screen readers stay
 * wherever focus was (often the sidebar link). After a route change, move focus
 * to the new page's heading so JAWS announces where the user landed.
 */
export default function RouteFocusManager(): null {
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return undefined;
    }
    previousPathnameRef.current = pathname;

    const frame = window.requestAnimationFrame(() => {
      const main = document.getElementById("main-content");
      if (!main) {
        return;
      }
      // The page may have placed focus itself (a dialog, an autofocus field).
      if (main.contains(document.activeElement) || document.querySelector('[aria-modal="true"]')) {
        return;
      }
      const heading = main.querySelector<HTMLElement>("h1");
      const target = heading ?? main;
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  return null;
}
